import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { useAuth, authFetch } from "@/lib/auth";
import { useFavourites } from "@/lib/favourites";
import { ListingCard } from "@/pages/Marketplace";

export default function Favourites() {
  const { user, loading, login } = useAuth();
  const { favs, loaded: favsLoaded } = useFavourites() || { favs: new Set(), loaded: false };
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => { document.title = "Favourites — Biker Boulevard"; window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    (async () => {
      try {
        const r = await authFetch("/api/favourites");
        if (r.ok) { const j = await r.json(); setItems(j.items || []); }
      } finally { setBusy(false); }
    })();
  }, [user, loading]);

  // Un-hearting a card removes it from this page (once favourite ids have loaded)
  const shown = favsLoaded ? items.filter((it) => favs.has(it.listing_id)) : items;

  if (loading || busy) {
    return <Shell><div className="pt-32 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#00cccc] mx-auto" /></div></Shell>;
  }
  if (!user) {
    return (
      <Shell>
        <div className="pt-32 max-w-md mx-auto text-center">
          <h2 className="text-2xl font-bold">Sign in to see your favourites</h2>
          <p className="mt-2 text-sm text-zinc-400">Tap the heart on any bike to save it here.</p>
          <button onClick={login} className="mt-6 px-6 py-3 rounded-full bg-[#00cccc] text-black font-semibold" data-testid="favourites-login">Continue with Google</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="pt-24 pb-20 max-w-[1200px] mx-auto px-5 md:px-10" data-testid="favourites-page">
        <span className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#00cccc]">// Saved Bikes</span>
        <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Your <span className="text-gradient-cyan">Favourites</span></h1>

        {shown.length === 0 ? (
          <div className="mt-8 p-10 rounded-2xl border border-white/10 bg-white/[0.03] text-center" data-testid="favourites-empty">
            <Heart className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="mt-4 text-lg font-semibold">No favourites yet.</p>
            <p className="mt-1 text-sm text-zinc-400">Tap the heart on any bike to save it here.</p>
            <Link to="/marketplace" className="mt-5 inline-block px-5 py-2.5 rounded-full border border-[#00cccc]/40 text-[#00cccc]" data-testid="favourites-browse-cta">Browse Marketplace</Link>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {shown.map((it) => <ListingCard key={it.listing_id} item={it} compact />)}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <main className="relative bg-[#0D0F12] text-white min-h-screen">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none -z-10"><div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(0,204,204,0.06), transparent 50%), #0D0F12" }} /></div>
      {children}
      <Footer />
    </main>
  );
}
